import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { GenerateForm, type PromptPreset } from "@/components/generate/generate-form";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { trackViewContent } from "@/services/analytics";
const TABS = [
  { href: "/create-character", label: "Create" },
  { href: "/animate-character", label: "Animate" },
  { href: "/extend-video", label: "Extend" },
];

const ANIMATE_PRESETS: PromptPreset[] = [
  {
    label: "Mid-air Clash",
    prompt: "two anime fighters collide mid-air, swords clashing with sparks flying, intense motion blur, dynamic camera rotation, dramatic lighting, sakuga style, impact frames, high energy",
  },
  {
    label: "Sword Draw",
    prompt: "anime swordsman slowly unsheathes katana, wind blowing, close-up shot, then sudden flash step forward, cinematic lighting, dramatic pause then burst of motion",
  },
  {
    label: "Cafe Sunset",
    prompt: "cozy anime café at sunset, warm golden light through windows, soft breeze moving curtains, character sitting quietly, lo-fi vibe, gentle camera pan",
  },
  {
    label: "Night Walk",
    prompt: "anime character walking alone under city lights, neon reflections on wet street, soft glow, cinematic tracking shot, calm atmosphere",
  },
  {
    label: "Power Awakening",
    prompt: "anime character begins glowing, energy aura expanding, hair and clothes flowing upward, camera slowly circling, light intensifying, dramatic transformation",
  },
  {
    label: "Demon Form",
    prompt: "character transforms into demon form, horns emerging, eyes glowing, dark aura spreading, energy pulsing, cinematic lighting, intense atmosphere",
  },
  {
    label: "Aura Burst",
    prompt: "anime character releases massive energy burst, shockwave expanding outward, debris flying, glowing aura, camera shake, high intensity sakuga style",
  },
  {
    label: "Goodbye Scene",
    prompt: "two anime characters facing each other at sunset, soft lighting, wind blowing, emotional expressions, slow camera pan, cinematic depth of field",
  },
  {
    label: "Anime Edit",
    prompt: "fast-paced anime edit style, quick cuts between scenes, dynamic poses, glowing effects, motion blur transitions, high energy, loopable ending",
  },
  {
    label: "Main Character",
    prompt: "anime character walking confidently toward camera, wind blowing, dramatic lighting, slow motion, main character energy, cinematic framing",
  },
  {
    label: "Transformation Loop",
    prompt: "anime character repeatedly transforming with glowing aura, seamless loop, energy pulses, smooth transitions, visually hypnotic",
  },
];

interface PingResponse {
  success: boolean;
  asset_url?: string;
  input_text?: string;
}

// Dedupe the partner-session claim across React StrictMode double-mounts and
// component remounts: one in-flight /ping per cid for the life of the page.
// The backend /ping is idempotent, so a real page refresh (fresh module state)
// simply re-claims the same session and re-issues the cookie.
const pingCache = new Map<string, Promise<PingResponse>>();
function claimPartnerSession(cid: string, fbclid: string | null): Promise<PingResponse> {
  let pending = pingCache.get(cid);
  if (!pending) {
    const params = new URLSearchParams({ cid });
    if (fbclid) params.set("fbclid", fbclid);
    pending = api
      .get<PingResponse>(`/ping?${params.toString()}`)
      .catch(() => ({ success: false }) as PingResponse);
    pingCache.set(cid, pending);
  }
  return pending;
}

export function ImageToVideoPage() {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("affiliate_click_id");
  const fbclid = searchParams.get("fbclid");
  const queryPrompt = searchParams.get("prompt") || "";

  useEffect(() => {
    trackViewContent("Animate Character", user?.id, { funnel_stage: "create" });
  }, []);

  const [prefill, setPrefill] = useState<{ imageUrl: string; prompt: string } | null>(null);
  const [loading, setLoading] = useState(!!cid);

  useEffect(() => {
    if (!cid) return;
    let cancelled = false;

    claimPartnerSession(cid, fbclid)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.asset_url) {
          setPrefill({ imageUrl: res.asset_url, prompt: res.input_text ?? "" });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [cid]);

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-6 pb-16">
      <div className="flex items-center gap-1 mb-6 border-b border-white/6 pb-3">
        {TABS.map((tab) => (
          <Link key={tab.href} to={tab.href}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-all",
              tab.href === "/animate-character"
                ? "bg-white/10 text-[#d4a574]"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5",
            )}>
            {tab.label}
          </Link>
        ))}
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          Animate Character
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Upload a character image to animate it, or just describe a scene to generate a video from text.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-6 animate-spin text-amber-600" />
        </div>
      ) : (
        <GenerateForm
          initialImageUrl={prefill?.imageUrl}
          initialPrompt={prefill?.prompt || queryPrompt}
          affiliateFunnel={!!cid}
          presets={ANIMATE_PRESETS}
        />
      )}

      <p className="text-[11px] text-muted-foreground mt-8 text-center">
        Need a character first? <Link to="/create-character" className="text-amber-700 font-semibold hover:underline">Create one</Link>
      </p>
    </div>
  );
}
